import React, { useCallback, useMemo, useState, useRef } from 'react';
import {
  LeftSection,
  TopSection,
  SubHeading,
  TitleSection,
  Heading,
  OuterDiv,
  Content,
  Form,
  Label,
  ItemDiv,
  ButtonSection,
  SkipButton,
  IconDiv,
  NotFound
} from '../projectDetails/style';
import { Dropdown, DropdownDivider, DropdownItem } from '../../../../../component/Dropdown';
import SVGIcon from '../../../../../assets/images/svg/SVGIcon';
import ButtonV2 from '../../../../../component/button/buttonV2';
import { DummySpan, Icon, Input, InputDiv, InputSectionDiv, ProfileSection, ProfileTag } from './style';
import Avatar from '../../../../../component/avatar/Avatar';
import { rootStore } from '../../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { isEmpty } from '../../../../../helpers/common';
import { CreateProjectPayloadInterfaceV2 } from '../../../../../interfaces/ProjectInterface';
import { useDispatch } from 'react-redux';
import { createNewProject, dispatchProjectSuccess } from '../../../../../services/projectServices';
import { useHistory } from 'react-router-dom';
import { PROJECT_ANALYTICS } from '../../../../../global/analyticsConstants';
import { trackAnalyticActivity } from '../../../../../services/analyticsService';
import { BUTTON_TYPE, PROJECT_ICON_TYPE } from '../../../../../global/constants';
import { useMobileDevice } from '../../../../../global/useMobile';

interface Props {
  setInviteModelOpen: () => void;
  // onClickBack: () => void;
}

function TeamMembers(props: Props) {
  //Props variables
  const { setInviteModelOpen } = props;
  //State variables
  const [search, setSearch] = useState('');
  const [buttonLoading, setButtonLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  //Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  //Mobx store variables
  const { workspaceStore, projectStore, currentUser } = rootStore;
  const { currentWorkspace } = workspaceStore;
  const { createProjectInput } = projectStore;
  //Other variables
  const dispatch = useDispatch();
  const history = useHistory();
  const isMobile = useMobileDevice();

  const scrollToItem = useCallback((index: number) => {
    if (itemRefs.current[index]) {
      itemRefs.current[index]?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, []);

  //handle Add members
  const handleProjectMember = useCallback(
    (userId: string) => {
      if (createProjectInput?.hasProjectUser(userId)) {
        createProjectInput.removeProjectUser(userId);
      } else {
        createProjectInput.addProjectUser(userId);
      }
      setSearch('');
      setCurrentIndex(0);
      if (!isMobile) setIsDropdownOpen(false);
    },
    [createProjectInput, isMobile]
  );

  //Handle create project
  const onclickInviteMember = useCallback(
    async (isSkip: boolean) => {
      if (!currentWorkspace?.id || !currentUser?.id) return;
      try {
        setButtonLoading(true);
        const payload: CreateProjectPayloadInterfaceV2 = {
          name: createProjectInput?.name,
          companyId: currentWorkspace?.id,
          startDate: createProjectInput?.startDate ? createProjectInput?.startDate.toISOString() : null,
          dueDate: createProjectInput?.dueDate ? createProjectInput?.dueDate.toISOString() : null,
          icon: {
            Type: createProjectInput?.icon?.type || PROJECT_ICON_TYPE.DEFAULT,
            Reference: createProjectInput?.icon?.reference || '',
            Color: createProjectInput?.icon?.color
          },
          projectUsers: !isEmpty(createProjectInput?.projectUsers) && !isSkip ? createProjectInput?.projectUsers : [],
          customerId: createProjectInput?.customerId || '',
          templateId: createProjectInput?.templateId || ''
        };

        const result = await dispatch(createNewProject(payload));
        if (result) {
          rootStore.projectStore.setCurrentProject(result?.data['_id']);
          trackAnalyticActivity(PROJECT_ANALYTICS.CREATED);
          history.push(`/projects/details/${result?.data['_id']}/tasks`);
          dispatchProjectSuccess('Project created successfully');
        }
      } catch (error) {
        console.log('error', error);
      } finally {
        setButtonLoading(false);
      }
    },
    [
      createProjectInput?.customerId,
      createProjectInput?.dueDate,
      createProjectInput?.icon?.color,
      createProjectInput?.icon?.reference,
      createProjectInput?.icon?.type,
      createProjectInput?.name,
      createProjectInput?.projectUsers,
      createProjectInput?.startDate,
      createProjectInput?.templateId,
      currentUser?.id,
      currentWorkspace?.id,
      dispatch,
      history
    ]
  );

  const selectedProjectUsers = useMemo(() => {
    return createProjectInput?.users?.map((user) => {
      return (
        <ProfileTag>
          <Avatar imgSrc={user?.profile_photo || ''} name={user?.name} size={18} isNotBorder={true} />
          {user.name}
          <Icon
            onClick={(e) => {
              e.stopPropagation();
              handleProjectMember(user.id);
            }}>
            <SVGIcon name='new-close-icon' width='12' height='12' viewBox='0 0 18 18' fill='var(--text-secondary)' />
          </Icon>
        </ProfileTag>
      );
    });
  }, [createProjectInput?.users, handleProjectMember]);

  const searchedUsers = useMemo(() => {
    const workspaceUsers = currentWorkspace?.users?.filter(
      (user) => !user?.id?.startsWith('agent_') && user?.id !== currentUser?.id
    );
    const searchNewText = search?.trim()?.toLowerCase();
    const words = searchNewText?.split(' ').filter((word) => word.length > 0);
    const searchedUsers = workspaceUsers?.filter((item) => {
      const title = item?.name.trim().toLowerCase() || '';
      return words.some((word) => title.includes(word));
    });
    const users = isEmpty(search) ? workspaceUsers : searchedUsers;
    return users || [];
  }, [currentUser?.id, currentWorkspace?.users, search]);

  const workspaceUsersList = useMemo(() => {
    const list =
      searchedUsers?.map((user, index) => {
        return (
          <div ref={(el) => (itemRefs.current[index] = el)} key={user.id}>
            <DropdownItem
              isActive={!isMobile && currentIndex === index}
              onClick={() => handleProjectMember(user.id)}
              label={user.name}
              isSelected={createProjectInput?.users?.includes(user)}
              isAvatar
              avatarSrc={user.profile_photo || ''}
            />
          </div>
        );
      }) || [];

    if (isEmpty(list)) {
      return <NotFound>No member found</NotFound>;
    }
    return list;
  }, [createProjectInput?.users, currentIndex, handleProjectMember, isMobile, searchedUsers]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex === 0 ? searchedUsers.length - 1 : prevIndex - 1;
            scrollToItem(newIndex);
            return newIndex;
          });
          break;
        case 'ArrowDown':
          e.preventDefault();
          setIsDropdownOpen(true);
          setCurrentIndex((prevIndex) => {
            const newIndex = prevIndex === searchedUsers.length - 1 ? 0 : prevIndex + 1;
            scrollToItem(newIndex);
            return newIndex;
          });
          break;
        case 'Enter':
          e.preventDefault();
          if (searchedUsers[currentIndex]) {
            handleProjectMember(searchedUsers[currentIndex].id);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setCurrentIndex(0);
          setIsDropdownOpen(false);
          setSearch('');
          break;
        default:
          break;
      }
    },
    [currentIndex, handleProjectMember, scrollToItem, searchedUsers]
  );

  return (
    <>
      <OuterDiv>
        <LeftSection>
          <TopSection>
            <TitleSection>
              {!isMobile && <Heading>Team members</Heading>}
              <SubHeading>Invite member to {createProjectInput?.name} project.</SubHeading>
            </TitleSection>
            <Content>
              <Form>
                <Label>Select Members</Label>
                <Dropdown
                  modalTitle='Members'
                  isMinWidth={398}
                  onOutsideClick={() => {
                    setCurrentIndex(0);
                  }}
                  updateDropdownOpen={setIsDropdownOpen}
                  closeOnItemClick={!isMobile}
                  content={
                    <InputSectionDiv>
                      <ProfileSection>
                        {selectedProjectUsers}
                        <InputDiv>
                          <DummySpan>
                            {selectedProjectUsers && selectedProjectUsers?.length > 0
                              ? `${search}|`
                              : 'Select members|'}
                          </DummySpan>
                          <Input
                            placeholder={
                              selectedProjectUsers && selectedProjectUsers?.length > 0 ? '' : 'Select members'
                            }
                            value={search}
                            onChange={(e) => {
                              setCurrentIndex(0);
                              setSearch(e.target.value);
                            }}
                            onKeyDown={handleKeyDown}
                          />
                        </InputDiv>
                      </ProfileSection>
                      <IconDiv>
                        <SVGIcon
                          name='new-angleDown-icon'
                          width='18'
                          height='18'
                          viewBox='0 0 18 18'
                          fill='var(--text-secondary)'
                          className={`angleDown-icon ${isDropdownOpen ? 'rotate' : ''}`}
                        />
                      </IconDiv>
                    </InputSectionDiv>
                  }
                  trigger='click'>
                  <ItemDiv ref={dropdownRef}>
                    {workspaceUsersList}
                    {false && (
                      <>
                        <DropdownDivider />
                        <DropdownItem
                          label='Invite new member'
                          iconName='member-plus-icon'
                          iconSize={20}
                          iconViewBox='0 0 18 18'
                          onClick={setInviteModelOpen}
                        />
                      </>
                    )}
                  </ItemDiv>
                </Dropdown>
              </Form>
            </Content>
          </TopSection>
          <ButtonSection>
            <ButtonV2
              title='Invite Teammates'
              variant={BUTTON_TYPE.PRIMARY}
              onClick={() => onclickInviteMember(false)}
              isLoading={buttonLoading}
            />
            <SkipButton as='button' onClick={() => onclickInviteMember(true)}>
              Skip
            </SkipButton>
          </ButtonSection>
        </LeftSection>
      </OuterDiv>
    </>
  );
}

export default observer(TeamMembers);
